/**
 * Centralized route-to-documentation mapping for TeeTime.
 *
 * This file is the single source of truth for in-app help links.
 * Every route should have a corresponding documentation entry.
 *
 * CI validates that all routes have entries here.
 * See: tools/scripts/validate-help-links.ts
 */

import type { DocsLinkConfig, HelpLinkEntry } from '@digiwedge/ui-docs-link';

/**
 * Route-to-documentation mapping.
 *
 * Routes are matched in order; first match wins.
 * Use '/*' suffix for wildcard matching (e.g., '/admin/*').
 */
export const HELP_LINKS: HelpLinkEntry[] = [
  // ─────────────────────────────────────────────────────────────────
  // Core User Pages
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/',
    docPath: '/intro',
    label: 'TeeTime Overview',
  },
  {
    route: '/login',
    docPath: '/getting-started/quick-start',
    label: 'Getting Started',
  },
  {
    route: '/signup',
    docPath: '/getting-started/quick-start',
    label: 'Getting Started',
  },
  {
    route: '/profile',
    docPath: '/booking/managing-bookings',
    label: 'My Bookings & Profile',
  },
  {
    route: '/settings',
    docPath: '/getting-started/quick-start',
    label: 'Settings Help',
  },
  {
    route: '/account/security',
    docPath: '/getting-started/quick-start',
    label: 'Account Security Help',
  },
  {
    route: '/preferences',
    docPath: '/getting-started/quick-start',
    label: 'Preferences',
  },

  // ─────────────────────────────────────────────────────────────────
  // Search & Discovery
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/search',
    docPath: '/booking/search',
    label: 'Search Help',
  },
  {
    route: '/courses-near-me',
    docPath: '/booking/search',
    label: 'Find Courses',
  },
  {
    route: '/specials',
    docPath: '/booking/overview',
    label: 'Specials & Offers',
  },
  {
    route: '/favourites',
    docPath: '/booking/search',
    label: 'Favourites',
  },

  // ─────────────────────────────────────────────────────────────────
  // Club & Tee Times
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/club/*',
    docPath: '/booking/making-bookings',
    label: 'Club & Tee Times',
  },
  {
    route: '/teetimes/*',
    docPath: '/booking/making-bookings',
    label: 'Book a Tee Time',
  },

  // ─────────────────────────────────────────────────────────────────
  // Bookings
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/booking/*',
    docPath: '/booking/managing-bookings',
    label: 'Booking Details',
  },
  {
    route: '/bookings/*',
    docPath: '/booking/managing-bookings',
    label: 'My Bookings',
  },
  {
    route: '/booking-success',
    docPath: '/booking/managing-bookings',
    label: 'Booking Confirmation',
  },
  {
    route: '/payment/*',
    docPath: '/booking/payments',
    label: 'Payment Help',
  },
  {
    route: '/payment-error',
    docPath: '/booking/payments',
    label: 'Payment Issues',
  },

  // ─────────────────────────────────────────────────────────────────
  // Waitlist
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/waitlist',
    docPath: '/booking/waitlist',
    label: 'Waitlist Help',
  },
  {
    route: '/waitlist/accept',
    docPath: '/booking/waitlist',
    label: 'Accept Waitlist Offer',
  },

  // ─────────────────────────────────────────────────────────────────
  // Buddies
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/buddies',
    docPath: '/booking/buddy-invites',
    label: 'Buddies',
  },
  {
    route: '/buddies/invite',
    docPath: '/booking/buddy-invites',
    label: 'Buddy Invites',
  },

  // ─────────────────────────────────────────────────────────────────
  // Tournaments & Scorecards
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/tournaments',
    docPath: '/tournaments/overview',
    label: 'Tournaments Help',
  },
  {
    route: '/tournament',
    docPath: '/tournaments/results',
    label: 'Tournament Leaderboard',
  },
  {
    route: '/tournament/detail',
    docPath: '/tournaments/entries',
    label: 'Tournament Details',
  },
  {
    route: '/tournament/draw',
    docPath: '/tournaments/entries',
    label: 'Tournament Draw',
  },
  {
    route: '/scorecard/*',
    docPath: '/tournaments/scoring',
    label: 'Scorecard Help',
  },

  // ─────────────────────────────────────────────────────────────────
  // Reviews
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/review',
    docPath: '/reviews/overview',
    label: 'Write a Review',
  },

  // ─────────────────────────────────────────────────────────────────
  // Wallet & Benefits
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/wallet',
    docPath: '/benefits/member-benefits',
    label: 'Wallet & Benefits',
  },

  // ─────────────────────────────────────────────────────────────────
  // Account & Auth
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/forgot-password',
    docPath: '/troubleshooting',
    label: 'Password Reset',
  },
  {
    route: '/reset-password/*',
    docPath: '/troubleshooting',
    label: 'Password Reset',
  },
  {
    route: '/verify-email/*',
    docPath: '/getting-started/quick-start',
    label: 'Email Verification',
  },
  {
    route: '/mfa',
    docPath: '/getting-started/quick-start',
    label: 'Two-Factor Authentication',
  },
  {
    route: '/mfa-setup',
    docPath: '/getting-started/quick-start',
    label: 'Two-Factor Authentication',
  },

  // ─────────────────────────────────────────────────────────────────
  // Support
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/contact',
    docPath: '/troubleshooting',
    label: 'Contact & Support',
  },
  {
    route: '/health',
    docPath: '/troubleshooting',
    label: 'System Status',
  },
];

export const DOCS_CONFIG: DocsLinkConfig = {
  baseUrl: 'https://teetime-docs.digiwedge.com',
  envUrlKey: 'VITE_DOCS_BASE_URL',
  windowUrlKey: '__TEETIME_DOCS_URL__',
  helpLinks: HELP_LINKS,
};
